import {Component, HostListener, OnInit} from "@angular/core";
import {LoggerService} from "../common/logger.service";
import {BaseComponent} from "../base.component";
import {UserService} from "../common/user.service";
import {MarkupService} from "../common/markup.service";
import {ReferralCreditService} from "./referral-credit.service";
import {IReferralCredit, ReferralCreditView} from "./referral-models";
import {Constants} from "../constants";
import {TranslateService} from "../translation/translate.service";
import {Util} from "../util/util";
import * as _ from 'lodash';
import {IShareLinkOptions} from "../social/social-models";
import {take} from "rxjs/operators";
import {UsanaCurrencyPipe} from "../currency/usana-currency.pipe";

@Component({
    selector: 'referral-credit',
    templateUrl: './referral-credit.component.html',
    styleUrls: ['./referral-credit.component.scss'],
    standalone: false
})
export class ReferralCreditComponent extends BaseComponent implements OnInit {

    referralCreditView: ReferralCreditView = new ReferralCreditView();
    currentReferralCredit: IReferralCredit;
    showSpinner: boolean;
    shareOptions: IShareLinkOptions;
    exceededTaxLimitMsg: string;
    isMobile: boolean = false;
    resizeTimeout: any;
    showingAllItems: boolean = false;

    // referral credit details line items management
    detailsLineItemsMax: number;
    detailsLineItemsMsg: string;
    detailsLineDetailsShowing: string;
    showDetailsLineItemsMsg: boolean;
    translationShowAll: string;
    translationShowLess: string;

    constructor(
        loggerService: LoggerService,
        private userService: UserService,
        private referralCreditService: ReferralCreditService,
        private translateService: TranslateService,
        private markupService: MarkupService,
        private currencyPipe: UsanaCurrencyPipe
    ) {
        super(userService, loggerService);
    }

    ngOnInit() {
        this.shareOptions = {shareType: Constants.SHARE_TYPE_LANDING, shareTag: 'cart.share.list.copy.to.clipboard', shareFrom: Constants.REFERRAL_CREDIT_STATE};
        Promise.all([
            this.translateService.translate('cart.referral.credits.details.show.all'),
            this.translateService.translate('cart.referral.credits.details.show.less')
        ]).then(([all, less]) => {

            this.translationShowAll = all;
            this.translationShowLess = less;
            this.markupService.setTitleFromTranslationTag("cart.referral.credits");

            this.resetCurrentDetailsListMax();
            this.getReferralCreditView();
            this.setMobile();
        });
    }

    @HostListener('window:resize')
    onWindowResize() {
        //debounce resize, wait for resize to finish before doing stuff
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout((() => {
            this.setMobile();
        }).bind(this), 500);
    }

    setMobile(){
        this.isMobile = Util.isMobile();
    }

    getReferralCreditView() {
        this.showSpinner = true;
        this.referralCreditService.getReferralCreditView().subscribe((response: any) => {
            this.showSpinner = false;
            if (response.success) {
                this.referralCreditView = response.message;
                _.forEach(this.referralCreditView.credits, (credit: IReferralCredit) => {
                    credit.lineItems = credit.lineItems.reverse();
                });
                this.setExceededTaxLimitMsg();
                if (this.referralCreditView.credits.length === 1) {
                    this.toggleDetailView(this.referralCreditView.credits[0]);
                }
            }
        }, (error: any) => {
            this.log.error(error);
            this.showSpinner = false;
        });
    }

    setExceededTaxLimitMsg() {
        if (!!this.referralCreditView && this.referralCreditView.exceededTaxLimit) {
            this.currencyPipe.transform(this.referralCreditView.taxLimit)
                .pipe(take(1))
                .subscribe((formatted: string) => {
                    const keyMap = new Map([[Constants.LIMIT_KEY, formatted]]);
                    this.translateService.translateRequest({tag: 'cart.referral.credits.tax.limit.message', replacements: keyMap})
                        .then((translation: string) => {
                            this.exceededTaxLimitMsg = translation;
                        });
                });
        }
    }

    isCurrentReferralCreditDetailView(referralCredit: IReferralCredit): boolean {
        return !!this.currentReferralCredit && this.currentReferralCredit.title == referralCredit.title;
    }

    resetCurrentDetailsListMax() {
        this.detailsLineItemsMax = Constants.REFERRAL_CREDIT_DETAILS_VIEW_MAX;
    }

    toggleDetailView(referralCredit: IReferralCredit) {
        if (this.isCurrentReferralCreditDetailView(referralCredit)) {
            this.currentReferralCredit = undefined;
        } else {
            this.currentReferralCredit = referralCredit;
            this.setShowDetailsLineItemsMsg();
        }
    }

    toggleDetailsLineItems(referralCredit: IReferralCredit) {
        if (this.detailsLineItemsMax == referralCredit.lineItems.length) {
            this.resetCurrentDetailsListMax();
        } else {
            this.detailsLineItemsMax = referralCredit.lineItems.length;
        }
        this.setShowDetailsLineItemsMsg();
    }

    setShowDetailsLineItemsMsg() {
        if (this.currentReferralCredit) {
            const lineItemsCount = this.currentReferralCredit.lineItems.length;
            if (lineItemsCount > Constants.REFERRAL_CREDIT_DETAILS_VIEW_MAX) {
                if (lineItemsCount > this.detailsLineItemsMax) {
                    this.showDetailsLineItemsMsg = true;
                    this.detailsLineItemsMsg = this.translationShowAll;
                    this.showingAllItems = false;
                } else if (lineItemsCount == this.detailsLineItemsMax) {
                    this.showDetailsLineItemsMsg = true;
                    this.detailsLineItemsMsg = this.translationShowLess;
                    this.showingAllItems = true;
                }
                if (this.showDetailsLineItemsMsg) {
                    const keyMap = new Map([[Constants.COUNT_KEY, this.detailsLineItemsMax], [Constants.LIMIT_KEY, lineItemsCount]]);
                    this.translateService.translateRequest({
                        tag: 'cart.referral.credits.details.showing',
                        replacements: keyMap
                    }).then((translation: string) => {
                        this.detailsLineDetailsShowing = '(' + translation + ')';
                    });
                }
            } else {
                this.showDetailsLineItemsMsg = false;
            }
        }
    }

    goToHub(){
        Util.redirectInternal(Constants.URL_HUB, Constants.URL_CREDIT_REFERRAL_LEARN_MORE, true);
    }
}
